import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, CheckCircle, AlertCircle, ArrowRight, Wind, Activity } from 'lucide-react';
import SiteHeaderV2 from '@/components/website/SiteHeaderV2';
import SiteFooterV2 from '@/components/website/SiteFooterV2';
import PageTitle from '@/components/website/PageTitle';

/* eslint-disable react/no-unescaped-entities */

// V2 Theme Colors
const C = {
  white: '#FFFFFF',
  cloud: '#F8FAFC',
  sky: '#4A6D8C',
  skyLight: '#DCE6F0',
  skyDark: '#334F6B',
  ink: '#0F172A',
  slate: '#475569',
  muted: '#94A3B8',
};

const AsthmaPage = () => {
  const navigate = useNavigate();

  return (
    <div 
      className="min-h-screen" 
      style={{ backgroundColor: C.white, fontFamily: 'Figtree, sans-serif' }}
    >
      {/* Global Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500&family=Figtree:wght@400;500;600&display=swap');
        @keyframes heroBreathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.08); }
        }
        @keyframes heroBreatheShadow {
          0%, 100% {
            box-shadow: 0 0 40px rgba(14, 165, 233, 0.30),
                        0 0 80px rgba(14, 165, 233, 0.10);
          }
          50% {
            box-shadow: 0 0 60px rgba(14, 165, 233, 0.50),
                        0 0 120px rgba(14, 165, 233, 0.20);
          }
        }
        .hero-breathing-orb {
          animation: heroBreathe 4s ease-in-out infinite,
                     heroBreatheShadow 4s ease-in-out infinite;
          will-change: transform, box-shadow;
        }
      `}</style>
      
      <SiteHeaderV2 />
      
      {/* Hero Section */}
      <section className="py-16 lg:py-24" style={{ backgroundColor: C.cloud }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <PageTitle
                kicker="Troubles respiratoires"
                title="Traitement Respiratoire de l’"
                highlight="Asthme"
                subtitle="La méthode Buteyko pour gérer l'asthme naturellement. Découvrez comment la rééducation respiratoire peut réduire significativement les symptômes de l'asthme."
                align="left"
                className="mb-8"
              />
              <Link 
                to="/evaluer-respiration"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-base font-medium transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                style={{ backgroundColor: C.sky, color: C.white }}
              >
                Évaluer votre respiration
                <ChevronRight size={18} />
              </Link>
            </div>
            <div className="flex justify-center">
              <div className="hero-breathing-orb w-72 h-72 lg:w-96 lg:h-96 rounded-full overflow-hidden" style={{ border: `4px solid ${C.skyLight}` }}>
                <img 
                  src="/lungs-asthma.jpg"
                  alt="Poumons - Illustration du traitement de l'asthme"
                  className="w-full h-full object-cover"
                  style={{ objectPosition: 'center 70%' }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="rounded-3xl rounded-2xl p-8 mb-8">
            <div className="flex items-start gap-4">
              <Wind className="w-8 h-8 text-sky-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-medium mb-4">Qu&apos;est-ce que la méthode Buteyko pour l&apos;asthme ?</h2>
                <p className="text-slate-700 leading-relaxed mb-4">
                  La méthode Buteyko pour l&apos;asthme est une technique de rééducation respiratoire développée par le Dr Konstantin Buteyko 
                  qui vise à <strong>corriger l&apos;hyperventilation chronique</strong> en apprenant à respirer lentement, calmement et par le nez.
                </p>
                <p className="text-slate-700 leading-relaxed">
                  Cette approche améliore l&apos;équilibre du CO₂ et de l&apos;oxygène dans le corps, ce qui peut <strong>réduire les symptômes 
                  et la dépendance aux médicaments</strong>, tout en restant complémentaire au traitement médical.
                </p>
              </div>
            </div>
          </div>

          <div className="border-l-4 border-sky-500 p-6 rounded-r-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-slate-900 mb-2">Important</p>
                <p className="text-slate-800 text-sm">
                  La méthode Buteyko est une <strong>technique complémentaire</strong> qui ne remplace pas le traitement médical. 
                  Consultez toujours un professionnel de santé avant de commencer cette méthode, car elle doit être intégrée 
                  à votre plan de traitement global.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction à la méthode Buteyko et hyperventilation */}
      <section className="py-12 rounded-3xl">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg">
            <p className="text-slate-700 leading-relaxed mb-4">
              La méthode Buteyko postule que l&apos;hyperventilation chronique (respirer trop d&apos;air) est une cause fondamentale 
              de l&apos;asthme, entraînant une baisse du taux de dioxyde de carbone (CO₂) dans le sang et provoquant une 
              constriction des voies respiratoires. Elle vise à rééduquer le patient pour réduire le volume respiratoire 
              via la respiration nasale et des pauses de respiration, améliorant ainsi les symptômes asthmatiques.
            </p>
            
            <h3 className="text-xl font-medium mb-4">
              Le lien entre hyperventilation et asthme selon Buteyko
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-cyan-500 mt-2 flex-shrink-0"></div>
                <p className="text-slate-700">
                  <strong>Hyperventilation = Carence en CO₂ :</strong> Respirer trop d&apos;air diminue le niveau de CO₂ 
                  dans le sang (hypocapnie), ce qui, selon le Dr Buteyko, provoque des spasmes bronchiques et une 
                  augmentation de la production de mucus, caractéristiques de l&apos;asthme.
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-cyan-500 mt-2 flex-shrink-0"></div>
                <p className="text-slate-700">
                  <strong>Mécanisme de défense erroné :</strong> Les asthmatiques ont souvent tendance à hyperventiler, 
                  ce qui aggrave leurs symptômes, créant un cercle vicieux, souligne le Centre médical de Boston.
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-cyan-500 mt-2 flex-shrink-0"></div>
                <p className="text-slate-700">
                  <strong>L&apos;Effet Bohr :</strong> Une respiration plus lente (diminution de la ventilation minute) 
                  augmente la concentration en CO₂, permettant une meilleure libération de l&apos;oxygène par l&apos;hémoglobine 
                  vers les tissus (effet Bohr), améliorant l&apos;oxygénation générale, explique la{' '}
                  <a 
                    href="https://breathing-academy.com/methode-buteyko-la-respiration-au-service-de-la-sante-de-la-physiologie-et-de-la-performance/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sky-600 hover:text-sky-700 underline font-medium"
                  >
                    Breathing Academy
                  </a>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Principes clés */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl mb-8" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            Les Principes Clés de la Méthode Buteyko
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="rounded-xl p-6 shadow-lg">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center mb-4">
                <AlertCircle className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-medium mb-3">Correction de l&apos;hyperventilation</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Le Dr Buteyko a observé que respirer trop profondément (hyperventiler) réduit le CO₂ sanguin, 
                ce qui peut provoquer un essoufflement et déclencher des crises d&apos;asthme.
              </p>
            </div>

            <div className="rounded-xl p-6 shadow-lg">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center mb-4">
                <Wind className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-medium mb-3">Respiration nasale</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Privilégier la respiration nasale plutôt que buccale pour réchauffer et humidifier l&apos;air, 
                et stimuler le monoxyde d&apos;azote (NO), bénéfique pour les voies respiratoires.
              </p>
            </div>

            <div className="rounded-xl p-6 shadow-lg">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center mb-4">
                <Activity className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-medium mb-3">Équilibre CO₂ et O₂</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                En stabilisant le niveau de CO₂, la méthode optimise l&apos;apport d&apos;oxygène aux cellules 
                et relaxe les muscles lisses des bronches, réduisant la bronchoconstriction.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Exercices et pratiques */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl mb-6" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            Exercices et Pratiques
          </h2>

          <div className="rounded-2xl p-8 shadow-lg border border-slate-100">
            <p className="text-slate-600 mb-6 leading-relaxed">
              La méthode Buteyko implique plusieurs exercices pour diminuer le souffle, assouplir le diaphragme 
              et réduire l&apos;excitation du centre respiratoire :
            </p>

            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-sky-50 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-sky-600 text-white flex items-center justify-center font-bold flex-shrink-0">1</div>
                <div>
                  <h3 className="font-medium mb-2">Réduction du souffle</h3>
                  <p className="text-slate-600 text-sm">
                    Apprendre à respirer moins, notamment lors d&apos;efforts quotidiens (parler, marcher). 
                    L&apos;objectif est de réduire progressivement le volume respiratoire minute.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-sky-50 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-sky-600 text-white flex items-center justify-center font-bold flex-shrink-0">2</div>
                <div>
                  <h3 className="font-medium mb-2">Pauses respiratoires (Control Pause)</h3>
                  <p className="text-slate-600 text-sm">
                    Retenir sa respiration confortablement après une expiration normale pour mesurer et améliorer 
                    son contrôle. Un score BOLT supérieur à 40 secondes indique une excellente tolérance au CO₂.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-sky-50 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-teal-600 text-white flex items-center justify-center font-bold flex-shrink-0">3</div>
                <div>
                  <h3 className="font-medium mb-2">Respiration abdominale</h3>
                  <p className="text-slate-600 text-sm">
                    Utiliser le diaphragme de manière plus efficace, en favorisant une respiration basse et calme 
                    plutôt qu&apos;une respiration thoracique haute et rapide.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-sky-50 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-sky-600 text-white flex items-center justify-center font-bold flex-shrink-0">4</div>
                <div>
                  <h3 className="font-medium mb-2">Bandeau buccal la nuit</h3>
                  <p className="text-slate-600 text-sm">
                    Utiliser un ruban adhésif médical léger (tape buccal) pour encourager la respiration nasale 
                    pendant le sommeil, réduisant ainsi la respiration buccale qui aggrave l&apos;hyperventilation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Efficacité et reconnaissance */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl mb-8" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}>
            Efficacité et Reconnaissance
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-xl p-6 shadow-lg">
              <h3 className="text-xl font-medium mb-4 flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-sky-600" />
                Approche complémentaire
              </h3>
              <p className="text-slate-600 text-sm mb-4 leading-relaxed">
                Ce n'est pas une cure miracle, mais une technique complémentaire validée. Les patients continuent 
                leur traitement médical, mais peuvent progressivement réduire leur consommation d'inhalateurs 
                de secours sous supervision médicale.
              </p>
              <div className="bg-sky-50 rounded-lg p-4">
                <p className="text-sm text-sky-800">
                  <strong>La méthode est reconnue par la GINA</strong> (Global Initiative for Asthma) 
                  comme une option viable de gestion de l'asthme.
                </p>
              </div>
            </div>

            <div className="rounded-xl p-6 shadow-lg">
              <h3 className="text-xl font-medium mb-4 flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-sky-600" />
                Bénéfices observés
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-sky-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">Réduction des symptômes d'asthme et des crises</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-sky-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">Amélioration de la qualité du sommeil</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-sky-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">Efficacité respiratoire accrue</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-sky-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">Diminution progressive de l'usage des bronchodilatateurs</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-sky-500 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">Meilleure tolérance à l'effort physique</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Call to action */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="bg-gradient-to-br from-sky-500 to-sky-600 rounded-2xl p-8 text-center text-white shadow-xl">
            <h2 className="text-2xl font-bold mb-4">Prêt à améliorer votre gestion de l'asthme ?</h2>
            <p className="mb-6 text-teal-50">
              Découvrez nos exercices de rééducation respiratoire basés sur la méthode Buteyko
            </p>
            <Button 
              onClick={() => navigate('/app-landing')} 
              className="bg-cyan-500 text-white hover:bg-cyan-600 rounded-full px-8 py-3 font-semibold shadow-lg"
            >
              Commencer les exercices
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </div>
        </div>
      </section>

      <SiteFooterV2 />
    </div>
  );
};

export default AsthmaPage;
